import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Mediaclip, Export, ExportChannel } from '../../../interfaces/mediaclip';
import { SapiService, SapiQueryResponse, SapiEntity } from '../../../services/sapi.service';
import { NgForm } from '@angular/forms';
import { OverlayService, DialogDefinition, NoticeDefinition } from '../../../services/overlay.service';

import { TitleCasePipe } from '@angular/common';

import dayjs from 'dayjs';
import { PublicationService } from '../../../services/publication.service';
import { Subscription } from 'rxjs';
import { WebSocketsService } from '../../../services/web-sockets.service';
import { UploadService } from '../../../services/upload.service';
import { Message } from 'app/interfaces/exportchannel';

export interface ExportStats {
	commentCount: string;
	dislikeCount: string;
	likeCount: string;
	viewCount: string;
	updatedDate: string;
}

export interface ExportSystemInfo {
	id: string;
	channelId?: string;
	remoteLogoImageUrl?: string;
	remoteBannerImageUrl?: string;
	authExpired?: boolean;
	message?: Message;
}

@Component({
	selector: 'mediaclip-details-export',
	templateUrl: './mediaclip-details-export.component.html',
	styleUrls: ['./mediaclip-details-export.component.scss']
})

export class MediaclipDetailsExportComponent implements OnInit, OnChanges, OnDestroy {

	@Input() form: NgForm;
	@Input() mediaclip: Mediaclip;
	@Input() isActive: boolean;

	youtubeExported: boolean;
	youtubeExportError: boolean;
	exportChannelDetails: ExportChannel;
	facebookExported: boolean;
	facebookExportError: boolean;
	facebookExportDetails: any[] = [];
	instagramExported: boolean;
	instagramExportedError: boolean;
	instagramSupported = true;
	instagramReason: string;
	instagramExportDetails: any[] = [];
	exportedStats: ExportStats;
	youtubeExportAfterImport = false;

	icon = 'cloud-download';
	importSource: string;
	importUrl: string;
	hasAutoCaptionsFromImport: string;
	hasAutoCaptionsFromExport: string;

	exportToYouTube: boolean;
	exportToFacebook: boolean;
	exportToInstagram: boolean;
	youtubeChannel: string;
	facebookPage: string;
	instagramPage: string;
	exportChannel: ExportChannel = {};
	disableYoutubeExport: boolean;
	exportQueueId: string;
	exportSystemName: string;
	channelSelected = false;
	channelIsAuthenticated = false;
	channelQuotaExceeded = false;
	resumeDate: any;

	private subscriptions: Subscription = new Subscription();

	constructor(
		private translateService: TranslateService,
		private sapiService: SapiService,
		private overlayService: OverlayService,
		private publicationService: PublicationService,
		private titlecasePipe: TitleCasePipe,
		private webSocketsService: WebSocketsService,
		private uploadService: UploadService) {
		this.exportChannel.youtube = [];
		this.exportChannel.facebook = [];
		this.exportChannel.instagram = [];
		this.exportToYouTube= false;
		this.exportToFacebook= false;
		this.exportToInstagram= false;
		this.exportChannelDetails = {};
	}

	ngOnInit() {
		const params = { q: 'status:active AND -authtokenid:""', sort: 'name asc' };
		this.sapiService.getUrl('/exportchannel', params).subscribe((result: any) => {
			if (result && result.items) {
				result.items.forEach((item) => {
					if (item.subtype === 'YouTube') {
						this.exportChannel.youtube.push({
							value: item.id,
							label: item.name
						});
					} else if (item.subtype === 'Facebook') {
						this.exportChannel.facebook.push({
							value: item.id,
							label: item.remotePageTitle
						});
						// set default value fb channel picker
						if (!this.facebookPage) {
							this.facebookPage = item.id;
							this.onChannelSelected(item.id);
						}
					} else if (item.subtype === 'Instagram') {
						this.exportChannel.instagram.push({
							value: item.id,
							label: item.remotePageTitle
						});
						// set default value fb channel picker
						if (!this.instagramPage) {
							this.instagramPage = item.id;
							this.onChannelSelected(item.id);
						}
					}
				});
			}
		});

		this.exportedStats = {
			commentCount: '0',
			dislikeCount: '0',
			likeCount: '0',
			viewCount: '0',
			updatedDate: ''
		};

		this.subscriptions.add(this.webSocketsService.subscribeToChannel('export').subscribe((websocketEvent: any) => {
			if (websocketEvent && websocketEvent.Object && websocketEvent.ev) {

				if (websocketEvent.payload && websocketEvent.payload.id === this.mediaclip.id) {
					// Hoping this would work to manually override Facebook_status
					if (this.mediaclip.Facebook_export) {
						websocketEvent.payload.Facebook_export = true;
					}
					this.mediaclip = websocketEvent.payload;

					if (websocketEvent.ev === 'exportItemPublished' || websocketEvent.ev === 'exportItemUploading') {
						this.updateExportedClip(websocketEvent.payload);
					}
					if (websocketEvent.ev === 'exportItemError') {
						this.exportToYouTube= false;
						this.youtubeExported = false;
						this.facebookExported = false;
						this.instagramExported = false;
						this.disableYoutubeExport = false;
						this.youtubeExportError = true;
						this.facebookExportError = true;
						this.instagramExportedError = true;

						const notice: NoticeDefinition = {
							icon: 'warning',
							title: this.translateService.instant('ERROR'),
							type: 'error',
							contents: this.translateService.instant('ERROR_EXPORTING_CLIP_SYSTEM', {
								clipId: this.mediaclip.id,
								exportSystemName: this.exportSystemName,
								errorMessage: (websocketEvent.message ? websocketEvent.message : '')
							})
						};
						this.overlayService.createNotice(notice);
					}
					if (websocketEvent.ev === 'exportItemDeleted') {
						this.exportToYouTube= false;
						this.youtubeExported = false;
						this.disableYoutubeExport = false;
						this.youtubeChannel = ''; // Resets channel so user starts with a 'clean slate'
						this.uploadService.onExportItemDeleted(this.mediaclip); // Removes the reference to this file in the box
					}
				}
			}
		}));

		this.canExportToInstagram();
	}

	updateExportedClip(exportedClip: Mediaclip) {
		// reset fb overview
		this.facebookExportDetails = [];
		this.instagramExportDetails = [];

		exportedClip.exports.forEach((exported: Export) => {
			exported.stats = {
				commentCount: '0',
				dislikeCount: '0',
				likeCount: '0',
				viewCount: '0',
				updatedDate: ''
			};
			if (exported.exportchannel.exportsystemname === 'YouTube') {
				this.youtubeExported = exported.status !== 'remove' && exported.status !== 'error';
				this.youtubeExportError = exported.status === 'error';
				this.exportChannelDetails = exported.exportchannel;
				this.exportQueueId = exported.id;
				if (this.exportChannelDetails) {
					this.youtubeChannel = exported.exportchannel.id;
				}
				if (exported.status === 'exported') {
					this.sapiService.get('commonclip', this.mediaclip.id, 'youtubeMetrics').subscribe((stats: any) => {
						this.exportedStats = {
							commentCount: this.translateCount(stats.commentCount),
							dislikeCount: this.translateCount(stats.dislikeCount),
							likeCount: this.translateCount(stats.likeCount),
							viewCount: this.translateCount(stats.viewCount),
							updatedDate: stats.updateddate
						};
					});
				}
				if (exported.hasAutomatedCaptions) {
					this.hasAutoCaptionsFromExport = this.checkHasCaptions(exported.hasAutomatedCaptions);
				}
			} else if (exported.exportchannel.exportsystemname === 'Facebook') {
				this.facebookExported = exported.status !== 'remove' && exported.status !== 'error';
				this.facebookExportError = exported.status === 'error';
				if (exported.externalid) {
					this.sapiService.get('commonclip', this.mediaclip.id, 'socialMetrics').subscribe((stats: any) => {
						for (let i = 0; i < stats.exports.length; i++) {
							if (exported.externalid === stats.exports[i].externalid) {
								exported.stats = {
									commentCount: this.translateCount(stats.exports[i].stats.comments_count),
									dislikeCount: 'n/a',
									likeCount: this.translateCount(stats.exports[i].stats.like_count),
									viewCount: this.translateCount(stats.exports[i].stats.views),
									updatedDate: stats.exports[i].updateddate
								};
							}
						}
					});
				}
				this.facebookExportDetails.push(exported);
			} else if (exported.exportchannel.exportsystemname === 'Instagram') {
				this.instagramExported = exported.status !== 'remove' && exported.status !== 'error';
				this.instagramExportedError = exported.status === 'error';
				if (exported.externalid) {
					this.sapiService.get('commonclip', this.mediaclip.id, 'socialMetrics').subscribe((stats: any) => {
						for (let i = 0; i < stats.exports.length; i++) {
							if (exported.externalid === stats.exports[i].externalid) {
								exported.exportchannel.remoteUrl = stats.exports[i].stats.permalink;
								exported.stats = {
									commentCount: this.translateCount(stats.exports[i].stats.comments_count),
									dislikeCount: 'n/a',
									likeCount: this.translateCount(stats.exports[i].stats.like_count),
									viewCount: this.translateCount(stats.exports[i].stats.video_insights[0].values[0].value),
									updatedDate: stats.exports[i].updateddate
								};
							}
						}
					});
				}
				this.instagramExportDetails.push(exported);
			}
		});
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.mediaclip && simpleChanges.mediaclip.currentValue) {

			if (this.mediaclip.exports && this.mediaclip.exports.length) {
				this.updateExportedClip(this.mediaclip);
			} else {
				// clip is not exported to YT
				this.youtubeExported = false;
			}

			let embargoActive = false;
			if (this.mediaclip.embargoFrom || this.mediaclip.embargoTo) {
				if (this.mediaclip.embargoFrom) {
					if (dayjs().isBefore(dayjs(this.mediaclip.embargoFrom))) {
						embargoActive = true;
					}
				}
				if (this.mediaclip.embargoTo) {
					if (dayjs().isAfter(dayjs(this.mediaclip.embargoTo))) {
						embargoActive = true;
					}
				}
			}

			this.publicationService.getData().subscribe((publication: any) => {
				if (publication) {
					this.youtubeExportAfterImport = publication.youtubeExportAfterImport;
				}
			});
			if (this.mediaclip && this.mediaclip.status === 'draft') {
				this.exportToYouTube= false;
			}
			if (this.mediaclip && this.mediaclip.youtubeImportId) {
				// control import information
				let importSrc: string;

				if (this.mediaclip.importSource) {
					// the clean, new way
					importSrc = this.mediaclip.importSource;
					if (importSrc.indexOf('twitch') !== -1) {
						importSrc = 'twitch';
					}
				} else if (this.mediaclip.sourceid.indexOf('youtu') !== -1) {
					importSrc = 'youtube';
				}
				if (this.mediaclip.importUrl) {
					// the clean, new way
					this.importUrl = this.mediaclip.importUrl;
				} else if (this.mediaclip.sourceid) {
					// old imported clips with sourceid
					this.importUrl = this.mediaclip.sourceid.replace('imported-from:', '');
				}

				const importSites = ['apple', 'dropbox', 'facebook', 'flickr', 'instagram', 'soundcloud', 'twitch', 'vimeo', 'vine', 'youtube'];
				if (importSites.indexOf(importSrc) !== -1) {
					switch (importSrc) {
						case 'facebook':
							this.icon = 'facebook-official';
							break;
						case 'vimeo':
							this.icon = 'vimeo-square';
							break;
						default:
							this.icon = importSrc;
							break;
					}
				}

				switch (importSrc) {
					// camelcasing for soundcloud en youtube
					case 'soundcloud':
						this.importSource = 'SoundCloud';
						break;
					case 'youtube':
						this.importSource = 'YouTube';
						break;
					default:
						this.importSource = this.titlecasePipe.transform(importSrc);
						break;
				}
			}
			this.disableYoutubeExport = (this.youtubeExported && this.mediaclip.YouTube_status !== 'error') || embargoActive ||
				(typeof this.mediaclip.youtubeImportId !== 'undefined' && this.mediaclip.youtubeImportId.length > 0
					&& (this.importSource === 'YouTube' && !this.youtubeExportAfterImport));

			if (this.importSource === 'YouTube' && this.mediaclip && this.mediaclip.import && this.mediaclip.import.hasCaptions) { // only for YouTube for now
				this.hasAutoCaptionsFromImport = this.checkHasCaptions(this.mediaclip.import.hasCaptions);
			}
		}
	}

	ngOnDestroy() {
		this.webSocketsService.unsubscribeFromChannel('export');
		this.subscriptions.unsubscribe();
	}

	translateCount(value: string) {
		let num: number = parseInt(value, 10) || 0;
		const units = [
			'',
			'K',
			'M',
			'B',
			'T'
		];
		let unitIndex = 0;
		while (num >= 1000 && unitIndex < 5) {
			unitIndex++;
			num = num / 1000;
		}
		return num + units[unitIndex];
	}

	deleteFromChannel(channel: string) {
		if (channel) {
			const siteName = { site: 'YouTube' };
			const dialog: DialogDefinition = {
				icon: 'trash',
				title: this.translateService.instant('DELETE_FROM_SITE', siteName),
				contents: this.translateService.instant('DELETE_SOCIAL_CONFIRM', siteName),
				onConfirm: () => {
					this.overlayService.setLoading(true);
					this.sapiService.delete('exportchannel', channel, 'mediaclip/' + this.mediaclip.id).subscribe(() => {
						this.exportToYouTube= false;
						this.youtubeExported = false;
						this.disableYoutubeExport = true;
						this.youtubeChannel = ''; // Resets channel so user starts with a 'clean slate'
						this.uploadService.onExportItemDeleted(this.mediaclip); // Removes the reference to this file in the box
						this.overlayService.setLoading(false);
					}, error => {
						this.overlayService.createToast(this.translateService.instant('EXPORT_REMOVE_FAILED'), 'warning');
						this.overlayService.setLoading(false);
					});
				}
			};

			this.overlayService.createDialog(dialog);
		}
	}

	onChannelSelected(exportChannelId: string) {
		this.channelQuotaExceeded = false;
		this.channelIsAuthenticated = true;
		this.sapiService.get('exportchannel', exportChannelId, 'getExportSystemInfo').subscribe((exportSystemInfo: ExportSystemInfo) => {
			this.channelSelected = true;
			if (exportSystemInfo) {
				this.exportChannelDetails.remoteLogoImageUrl = exportSystemInfo.remoteLogoImageUrl;
				this.exportChannelDetails.remoteBannerImageUrl = exportSystemInfo.remoteBannerImageUrl;
				if (exportSystemInfo.message?.code === 'QUOTUM_EXCEEDED') {
					this.resumeDate = exportSystemInfo.message.data.resumeDate;
					this.channelQuotaExceeded = true;
				}
				if (exportSystemInfo.authExpired) {
					this.channelIsAuthenticated = false;
				} else {
					this.channelIsAuthenticated = true;
				}
			}
		});
	}

	exportToChannel(channel: string, exportType?: string) {
		if (exportType === 'facebook') {
			this.mediaclip.Facebook_export = true;
		}

		this.sapiService.postUrl('/exportchannel/' + channel + '/mediaclip/' + this.mediaclip.id).subscribe((entity: SapiEntity) => {
			if (!exportType) {
				this.exportSystemName = 'YouTube';
				this.uploadService.onExport(this.mediaclip);
			} else {
				let data = {};
				if (exportType === 'facebook') {
					this.exportSystemName = 'Facebook';
					data = { ...entity, type: 'MediaClip', Facebook_status: true, status: 'uploading', title: this.mediaclip.title, originalfilename: this.mediaclip.originalfilename };
				} else if (exportType === 'instagram') {
					this.exportSystemName = 'Instagram';
					data = { ...entity, type: 'MediaClip', Instagram_status: true, status: 'uploading', title: this.mediaclip.title, originalfilename: this.mediaclip.originalfilename };
				}
				// entity is the exportQueue item
				// because a mediaClip can have multiple FB/IG exports, we listen to the unique exportQueue id instead of mediaclip ID
				this.uploadService.onExport(data);
			}
		});
	}

	canExportToInstagram() {
		// Everything allowed and not, if we don't know we let Instagram decide:
		// https://developers.facebook.com/docs/instagram-api/reference/ig-user/media/

		if (this.mediaclip.length > 60 || this.mediaclip.length < 3) {
			this.instagramSupported = false;
			this.instagramReason = this.translateService.instant('INSTAGRAM_CLIP_LENGTH');
			return;
		}
		for (let i = 0; i < this.mediaclip.assets.length; i++) {
			if (this.mediaclip.assets[i].mediatype === 'MP4_HD') {
				const filesize = parseInt(this.mediaclip.assets[i].filesize, 10);
				if (filesize > 100000000) {
					this.instagramSupported = false;
					this.instagramReason = this.translateService.instant('INSTAGRAM_FILESIZE');
					return;
				}
			}
		}
		if (this.mediaclip.dar < 4/5 || this.mediaclip.dar > 16/9) {
			this.instagramSupported = false;
			this.instagramReason = this.translateService.instant('INSTAGRAM_ASPECT_RATIO');
			return;
		}
	}

	checkHasCaptions(hasCaptions: string) {
		let hasAutomatedCaptions;
		switch (hasCaptions) {
			case 'new':
				hasAutomatedCaptions = this.translateService.instant('QUEUED_FOR_IMPORT');
				break;
			case 'yes':
				hasAutomatedCaptions = this.translateService.instant('IMPORTED');
				break;
			case 'retry':
			case 'try':
				hasAutomatedCaptions = this.translateService.instant('SCHEDULED_FOR_IMPORT');
				break;
			case 'never':
				hasAutomatedCaptions = this.translateService.instant('NOT_AVAILABLE');
				break;
			default:
				break;
		}
		return hasAutomatedCaptions;
	}
}
